import Avatar from "@mui/material/Avatar";
import { OLPaper } from "OLPaper";
import { hasAnyAccessToResource, hasModifyAccessToResource } from "api/general";
import { BoxSection } from "components/BoxSection/BoxSection";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { ReactComponent as CalendarHeart01Icon } from "components/Icons/calendar-heart-01.svg";
import { ReactComponent as CalendarIcon } from "components/Icons/calendar.svg";
import { ReactComponent as CreditCard02Icon } from "components/Icons/credit-card-02.svg";
import { ReactComponent as Edit03Icon } from "components/Icons/edit-03.svg";
import { ReactComponent as FaceIdIcon } from "components/Icons/face-id.svg";
import { ReactComponent as Mail01Icon } from "components/Icons/mail-01.svg";
import { ReactComponent as PhoneIcon } from "components/Icons/phone.svg";
import { ReactComponent as Trophy01Icon } from "components/Icons/trophy-01.svg";
import { ReactComponent as User01Icon } from "components/Icons/user-01.svg";
import { ReactComponent as UsersPlusIcon } from "components/Icons/users-plus.svg";
import { StatusValue } from "components/Table/StatusValue";
import { Tooltip } from "components/Tooltips/Tooltip";
import { isValid, parseISO } from "date-fns";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { useIsOverflown } from "hooks/useIsOverflown";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useParams } from "react-router-dom";
import { BackendMemberModel } from "types/members/main";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { getAcronym, pluralizeUnit } from "utilities/strings";
import { Modals } from "../MemberList/MemberList";
import { MemberModals } from "../MemberList/MemberModals";
import { useStyles } from "./SingleMember.styles";
import { calculateDaysUntilNextBirthDate } from "./helpers";

interface Props {
  membersData?: BackendMemberModel;
}

export const UserProfileBar = ({ membersData }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { observeElement, isOverflown } = useIsOverflown();
  const contextMenu = useContextMenu();
  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    member: string;
    mode: number;
  }>();
  const { id: memberID } = useParams<{ id: string }>();

  const isMemberAnonymized = membersData?.anonymized;
  const hasModifyCustomerPermission = hasModifyAccessToResource("CUSTOMER");
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const hasModifyTransactionPermission =
    hasModifyAccessToResource("TRANSACTION");

  const getDaysUntilNextBirthDate = (birthDate: string) => {
    if (
      birthDate &&
      typeof birthDate === "string" &&
      isValid(parseISO(birthDate))
    ) {
      const numberOfDays = calculateDaysUntilNextBirthDate(birthDate);

      const label =
        numberOfDays === 0
          ? message({ id: "today" })
          : `${message({ id: "in" }).toLowerCase()} ${pluralizeUnit(
              numberOfDays,
              "day",
              "days",
            )}`;

      return ` (${label})`;
    } else {
      return "";
    }
  };

  const profileData = membersData
    ? [
        {
          copy: true,
          dataHjSupress: true,
          dataTestId: "single-member-member-id",
          icon: <User01Icon className={classes.icon} />,
          label: message({ id: "memberID" }),
          value: membersData.customerId,
        },
        {
          copy: true,
          dataHjSupress: true,
          dataTestId: "single-member-member-email",
          icon: <Mail01Icon className={classes.icon} />,
          label: message({ id: "email" }),
          value: membersData.email,
        },
        {
          copy: !!membersData.phone,
          dataHjSupress: true,
          dataTestId: "single-member-member-phone-number",
          icon: <PhoneIcon className={classes.icon} />,
          label: message({ id: "phoneNumber" }),
          value: membersData.phone,
        },
        {
          copy: !!membersData.loyaltyCardNumber,
          dataHjSupress: true,
          dataTestId: "single-member-member-loyalty-card-number",
          icon: <CreditCard02Icon className={classes.icon} />,
          label: message({ id: "loyaltyCardNumber" }),
          value: membersData.loyaltyCardNumber,
        },
        {
          copy: true,
          dataTestId: "single-member-member-registration-date",
          icon: <CalendarIcon className={classes.icon} />,
          label: message({ id: "registrationDate" }),
          value: OLDate(membersData.registeredAt, {
            formatString: "dd MMM, yyyy",
          }),
        },
        {
          copy: !!membersData.birthDate,
          dataHjSupress: true,
          dataTestId: "single-member-member-birthday-date",
          icon: <CalendarHeart01Icon className={classes.icon} />,
          label: message({ id: "dateOfBirth" }),
          value: `${OLDate(membersData.birthDate, {
            formatString: "dd MMM, yyyy",
          })}${getDaysUntilNextBirthDate(membersData?.birthDate || "")}`,
        },
        {
          copy: true,
          dataHjSupress: true,
          dataTestId: "single-member-member-referral-token",
          icon: <UsersPlusIcon className={classes.icon} />,
          label: message({ id: "referralToken" }),
          value: membersData.referralToken,
        },
        {
          copy: true,
          dataHjSupress: false,
          dataTestId: "single-member-member-gender",
          icon: <FaceIdIcon className={classes.icon} />,
          label: message({ id: "gender" }),
          value: message({ id: membersData.gender }),
        },
      ]
    : [];

  return (
    <OLPaper className={classes.userData}>
      <div className={classes.mainDataContainer}>
        {membersData ? (
          <Avatar
            alt={`${membersData.firstName} ${membersData.lastName}`}
            className={classes.avatar}
            variant="square"
            data-hj-suppress
          >
            {getAcronym(
              isMemberAnonymized
                ? message({ id: "undefined" })
                : `${membersData.firstName} ${membersData.lastName}`,
            )}
          </Avatar>
        ) : (
          <Skeleton height={48} width={49} />
        )}
        <div className={classes.hiddenOverflow}>
          <div className={classes.headerContainer}>
            {membersData ? (
              isMemberAnonymized ? (
                <div className={classes.userHeader}>
                  {message({ id: "undefined" })}
                </div>
              ) : membersData.firstName || membersData.lastName ? (
                <div
                  className={classes.userHeader}
                  data-test-id="single-member-member-name-label"
                  data-hj-suppress
                  ref={observeElement}
                >
                  <Tooltip
                    text={`${membersData?.firstName} ${membersData?.lastName}`}
                    disableHoverListener={!isOverflown}
                    placement="bottom-start"
                  >
                    <span>
                      {membersData.firstName} {membersData.lastName}
                    </span>
                  </Tooltip>
                </div>
              ) : null
            ) : (
              <Skeleton width={100} />
            )}
            <StatusValue status={membersData?.active} />
            {membersData?.currentLevel?.name && (
              <div className={classes.tier}>
                <Trophy01Icon className={classes.tierIcon} />
                {membersData.currentLevel.name}
              </div>
            )}
          </div>
          <div className={`${classes.smallText}`}>
            {membersData ? membersData.email : <Skeleton width={100} />}
          </div>
        </div>
        <SubheaderWrapper alignRight>
          <SubheaderLinkButton
            data-test-id="edit"
            disabled={!hasModifyCustomerPermission}
            icon={<Edit03Icon />}
            iconOnly
            link={links.editMember(memberID as string)}
            prevPath={links.singleMemberGeneralInfo(memberID)}
            variant="outlined"
          />
          <SubheaderMenuButton
            text={message({ id: "actions" })}
            menuItems={[
              {
                text: message({ id: "activateAccount" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.Activate,
                  });
                },
                hideCondition:
                  membersData?.active || !hasModifyCustomerPermission,
              },
              {
                hideCondition: !hasModifyTransactionPermission,
                text: message({ id: "addUnitsTransfer" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.Add,
                  });
                },
              },
              {
                hideCondition: !hasModifyTransactionPermission,
                text: message({ id: "deductUnitsTransfer" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.Deduct,
                  });
                },
              },
              {
                hideCondition:
                  membersData?.anonymized || !hasModifyCustomerPermission,
                text: message({ id: "anonymizeProfile" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.Anonymize,
                  });
                },
              },
              {
                hideCondition:
                  !hasModifyCustomerPermission || !hasAnyTierPermission,
                text: message({ id: "assignToTier" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.AssignToTier,
                  });
                },
              },
              {
                hideCondition: !hasModifyCustomerPermission,
                text: message({ id: "changePassword" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.ChangePassword,
                  });
                },
              },
              {
                hideCondition:
                  !membersData?.active || !hasModifyCustomerPermission,
                text: message({ id: "deactivateAccount" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.Deactivate,
                  });
                },
              },
              {
                hideCondition:
                  !membersData?.manuallyAssignedLevelId ||
                  !hasModifyCustomerPermission,
                text: message({
                  id: "removeManuallyAssignedTier",
                }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.RemoveManuallyAssignedTier,
                  });
                },
              },
              {
                hideCondition: !hasModifyCustomerPermission,
                text: message({ id: "removeProfile" }),
                onClick: () => {
                  contextMenu.handleMenuClose();
                  setOpen(true);
                  setModal({
                    member: memberID as string,
                    mode: Modals.Remove,
                  });
                },
              },
            ]}
            data-test-id="single-member-actions"
          />
        </SubheaderWrapper>
      </div>
      <MemberModals
        selectedModal={selectedModal}
        open={open}
        close={() => setOpen(false)}
        setModal={setModal}
      />
      <BoxSection items={profileData} />
    </OLPaper>
  );
};
